import { z } from 'zod';
import {
  articleListQuerySchema,
  articleTypeSchema,
  commodityGroupSchema,
  sourcingSchema,
} from './article.schema';

/**
 * Sammel-Bearbeitung im Artikelstamm (2026-09-07, Wunsch Felix): mehrere Artikel
 * in einem Zug ändern, statt jeden einzeln zu öffnen. Bei Altbeständen aus dem
 * Import stehen Typ, Lieferant, Warengruppe und Meldebestand reihenweise falsch
 * oder leer — einzeln wären das hunderte Male öffnen, ändern, speichern.
 *
 * Auswahl wie bei den Projekt-Sammelaktionen (Muster
 * `bulkUpdateProjectStatusSchema`): entweder explizite `ids` (die angehakten
 * Zeilen) ODER `allMatching: true` mit den Listenfiltern — dann wirkt die Aktion
 * auf ALLE zum Filter passenden Artikel, auch über Seitengrenzen hinweg. Die IDs
 * löst der Server aus dem Filter auf; der Client schickt bei „alle passenden"
 * keine (potenziell tausend) IDs mit.
 */

/**
 * Die Filterfelder der Artikelliste, ohne Blättern und Sortierung: bei
 * `allMatching` bestimmen genau sie die Zielmenge. Bewusst aus
 * `articleListQuerySchema` abgeleitet statt abgetippt — sonst driftet die
 * Zielmenge der Sammelaktion irgendwann von der angezeigten Liste ab.
 */
export const articleBulkFilterSchema = articleListQuerySchema
  .omit({ page: true, pageSize: true, sort: true })
  .partial();
export type ArticleBulkFilter = z.infer<typeof articleBulkFilterSchema>;

export const articleBulkSelectionSchema = articleBulkFilterSchema.extend({
  /** Angehakte Zeilen. Deckel wie bei den Projekten — darüber führt „alle passenden" hin. */
  ids: z.array(z.string().uuid()).max(5000).optional(),
  /** Alle zum Filter passenden Artikel (über Seitengrenzen hinweg). */
  allMatching: z.boolean().optional(),
});
export type ArticleBulkSelection = z.infer<typeof articleBulkSelectionSchema>;

/**
 * Sammelweise änderbare Artikelfelder — bewusst eine **Positivliste**, wie beim
 * Änderungsantrag (ADR 0089). Draußen bleiben:
 *
 * - **Nummer und Bezeichnung:** je Artikel verschieden, in einer Sammelaktion
 *   sinnlos (und die Nummer ist ohnehin unveränderlich).
 * - **Einheit:** ein Wechsel rechnet Bestand, Preise und Belegmengen NICHT um.
 *   Sammelweise wäre das ein stiller Datenschaden über hunderte Artikel.
 * - **Preise:** Geld gehört nicht in eine Massenänderung — dafür gibt es Staffeln
 *   und die Preishistorie je Artikel.
 * - **Bestand:** ändert sich nur über Bewegungen (Journal, ADR 0006), nie direkt.
 * - **Aktiv/Inaktiv:** hat mit „Deaktivieren" schon eine eigene Sammelaktion mit
 *   eigener Kaskade auf Ablängungen.
 *
 * `nullable` heißt: Feld leeren ist erlaubt (kein Lieferant, keine Warengruppe,
 * kein Meldebestand). `undefined`/fehlend heißt: Feld nicht anfassen.
 */
export const articleBulkFieldsSchema = z
  .object({
    /** Lagerartikel ⇄ projektbezogen — entscheidet über die Disposition. */
    articleType: articleTypeSchema,
    /** Eigenfertigung ⇄ Zukauf. */
    sourcing: sourcingSchema,
    /** Warengruppe (4-stellig, ADR 0016); null = keine. */
    commodityGroup: commodityGroupSchema.nullable(),
    /**
     * Standardlieferant als Soft-Ref + Name-Snapshot (wie im Artikelformular):
     * beide Felder gehören zusammen, `null` entfernt den Lieferanten.
     */
    defaultSupplierId: z.string().uuid().nullable(),
    defaultSupplierName: z.string().trim().max(200).nullable(),
    /** Meldebestand — ab hier schlägt die Ampel aus; null = keine Überwachung. */
    minStock: z.number().min(0).nullable(),
    /** Höchstbestand — bis hierhin füllt der Nachbestell-Vorschlag auf. */
    maxStock: z.number().min(0).nullable(),
    /**
     * Wiederbeschaffungszeit in Tagen — meist eine Eigenschaft des Lieferanten
     * und damit reihenweise gleich (genau der Fall für eine Sammelaktion).
     */
    leadTimeDays: z.number().int().min(0).max(365).nullable(),
    manufacturer: z.string().trim().max(200).nullable(),
    /** Ursprungsland als ISO-3166-1 alpha-2 (AT, DE, CN …). */
    countryOfOrigin: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{2}$/, 'ISO-Code mit 2 Buchstaben.')
      .nullable(),
  })
  .partial();
export type ArticleBulkFields = z.infer<typeof articleBulkFieldsSchema>;

/** Menschenlesbare Beschriftungen der Felder — eine Quelle für Dialog und Meldung. */
export const ARTICLE_BULK_FIELD_LABELS: Record<keyof ArticleBulkFields, string> = {
  articleType: 'Artikeltyp',
  sourcing: 'Beschaffung',
  commodityGroup: 'Warengruppe',
  defaultSupplierId: 'Standardlieferant',
  defaultSupplierName: 'Standardlieferant (Name)',
  minStock: 'Meldebestand',
  maxStock: 'Höchstbestand',
  leadTimeDays: 'Wiederbeschaffungszeit (Tage)',
  manufacturer: 'Hersteller',
  countryOfOrigin: 'Ursprungsland',
};

/** Mindestens eine Auswahlquelle muss gesetzt sein — sonst wäre die Zielmenge unklar. */
function hasSelection(selection: { ids?: string[]; allMatching?: boolean }): boolean {
  return selection.allMatching === true || (Array.isArray(selection.ids) && selection.ids.length > 0);
}

/**
 * Sammel-Änderung: Auswahl + die zu setzenden Felder. Artikel, bei denen keines
 * der gesetzten Felder einen anderen Wert bekäme, werden serverseitig
 * ausgefiltert: sie sind kein Fehler, sondern schlicht nicht gemeint — und
 * würden sonst Erfolgsbilanz und Änderungsprotokoll mit Nicht-Änderungen fluten.
 *
 * Die Felder liegen in `fields` und NICHT neben der Auswahl: Ein Artikelfeld und
 * ein gleichnamiger Listenfilter (`articleType`, `commodityGroup`, `manufacturer`,
 * `countryOfOrigin`, `sourcing` gibt es beides) würden sich sonst denselben Namen
 * teilen — „alle passenden" filterte dann auf den Zielwert und schlösse genau die
 * Artikel aus, die geändert werden sollen (Fehler beim ersten Bau, 2026-09-07).
 */
export const bulkUpdateArticlesSchema = articleBulkSelectionSchema
  .extend({
    fields: articleBulkFieldsSchema.refine((value) => Object.keys(value).length > 0, {
      message: 'Es muss mindestens ein Feld geändert werden.',
    }),
  })
  .refine(hasSelection, { message: 'Es muss mindestens ein Artikel ausgewählt sein.' })
  .refine(
    (value) =>
      value.fields.minStock == null ||
      value.fields.maxStock == null ||
      value.fields.maxStock >= value.fields.minStock,
    { message: 'Der Höchstbestand darf nicht unter dem Meldebestand liegen.', path: ['fields', 'maxStock'] },
  );
export type BulkUpdateArticlesInput = z.infer<typeof bulkUpdateArticlesSchema>;
